/**
 * The tapped-but-not-yet-committed bottom tab, held OUTSIDE React state.
 *
 * It used to be `useState` in `PrimaryNavigationLayout`, so every tab tap re-rendered the layout — and with
 * it the whole `Tabs` navigator, every scene wrapper and all five tab-bar items — once to set the
 * override and once more to clear it, on top of the navigator's own commit for the switch. Only
 * the tab bar reads this value, so only the tab bar subscribes to it.
 */
export type OptimisticActiveTabStore = {
  /** Drop the override and re-render the bar: the navigator went somewhere else. */
  clear: () => void;
  /**
   * Drop the override WITHOUT a render. For the moment the navigator has committed the very tab
   * the override predicted: the bar already paints that tab, so clearing changes nothing visible.
   */
  clearSilently: () => void;
  getSnapshot: () => string | null;
  set: (name: string) => void;
  subscribe: (listener: () => void) => () => void;
};

export function createOptimisticActiveTabStore(): OptimisticActiveTabStore {
  let activeName: string | null = null;
  const listeners = new Set<() => void>();
  const publish = (next: string | null) => {
    if (activeName === next) {
      return;
    }
    activeName = next;
    for (const listener of listeners) {
      listener();
    }
  };
  return {
    clear: () => {
      publish(null);
    },
    clearSilently: () => {
      activeName = null;
    },
    getSnapshot: () => activeName,
    set: publish,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
